import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input} from "@angular/core";
import {EventBusService} from "../../../services/event-bus.service";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../../config/hotel-tours-list-page.config";
import {DatePipe} from "@angular/common";
import {pluralize} from "../../pipes/pluralize/pluralize";
import {
  HotelToursResultResponseClassAvailable, HotelToursResultResponseSeatAvailable,
  HotelToursResultTour,
  HotelToursResultTourAirline
} from "../../../types/interfaces/hotel-tour-search";
import {WindowRefService} from "../../../services/window-ref.service";
import {HttpClient} from "@angular/common/http";
import {PriceFormatPipe} from "../../pipes/price-format/price-format.pipe";
import {HotelToursSearchService} from "../../../services/search/hotel-tours-search.service";
import {catchError, Observable, throwError} from "rxjs";
import {map} from "rxjs/operators";
import {BrandList} from "../../../models";
import {environment} from "../../../../environments/environment";

@Component({
  selector: 'app-hotel-tour',
  templateUrl: environment.mobile ? './hotel-tour.component.mobile.html' : './hotel-tour.component.html',
  styleUrls: environment.mobile ? ['./hotel-tour.component.mobile.scss'] : ['./hotel-tour.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [PriceFormatPipe]
})
export class HotelTourComponent {
  @Input() tour: HotelToursResultTour;

  isPreview: boolean = false;

  constructor(
    private http: HttpClient,
    private eventBus: EventBusService,
    private cdRef: ChangeDetectorRef,
    private windowService: WindowRefService,
    private searchService: HotelToursSearchService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig
  ) {
    this.isPreview = this.windowService.nativeWindow.app.isPreview;
  }

  public get checkInDate() {
    const date = new DatePipe('ru').transform(this.tour.dateFrom, 'd MMM');
    return date.replace('.', '');
  }

  public get isHt() {
    return this.hotelToursPageConfig.brandId === 1;
  }

  public get checkOutDate() {
    const date = new DatePipe('ru').transform(this.tour.dateTo, 'd MMM');
    return date.replace('.', '');
  }

  get isTrackingTour(): boolean {
    const hash = this.tour?.hash;

    if (!hash) {
      return false;
    }

    return !!this.hotelToursPageConfig.trackedTourHashes?.[hash];
  }

  get trackingId(): string | null {
    const hash = this.tour?.hash;

    if (!hash) {
      return null;
    }

    return this.hotelToursPageConfig.trackedTourHashes?.[hash] || null;
  }

  get tourHunterIconSrc(): string {
    return this.isTrackingTour
      ? '/x-assets/img/icons/icon-filled-heart.svg'
      : '/x-assets/img/icons/icon-unfilled-heart-blue.svg';
  }

  get tourHunterIconAlt(): string {
    return this.isTrackingTour ? 'Unlike' : 'Like';
  }

  public getDaysLabel(value) {
    return pluralize(value, ['день', 'дня', 'дней'])
  }

  public getNightsLabel(value) {
    return pluralize(value, ['ночь', 'ночи', 'ночей'])
  }

  public get getCashback() {
    const price = this.tour.price.value;
    return this.calculateCashbackForTour(price);
  }

  public get getOldPrice() {
    const price = this.tour.price.value;
    const discount = this.tour.discount;
    if (!discount) {
      return null;
    }
    return Math.round(price * (100 + discount) / 100);
  }

  get btnToTourDetailsLabel(): string {
    return this.isHotelsSearch ? "Выбрать" : this.hotelToursPageConfig.btnToTourDetailsLabel;
  }

  private calculateCashbackForTour(tourPrice: number): number {


    if (this.hotelToursPageConfig.brandId === BrandList.HT_KZ) {
      const maxCashback = 120000;
      const minCashback = 15000;
      // Если тур стоит 1кк и более, то за каждые 500,000 тенге клиент получает 6,850 бонусов, но не менее 15,000 тенге
      if (tourPrice > 1000000) {
        const cashback = Math.floor(tourPrice / 500000) * 6850;
        return Math.min(Math.max(cashback, minCashback), maxCashback);
      }

      // Если тур стоит <= 1,000,000 тенге, то 2% от стоимости тура, но не более 15,000 тенге
      const cashback = Math.ceil(tourPrice * 2 / 100);
      return Math.min(cashback, minCashback);
    }

    if (this.hotelToursPageConfig.brandId === BrandList.BRONIX_UZ) {
      const minCashback = 337829;
      const cashback = Math.ceil(tourPrice * 2 / 100);
      return Math.min(cashback, minCashback);
    }
  }

  public shareTourBtnClick(event: MouseEvent): void {
    event.stopPropagation();
    const tourId = this.tour.id;

    if (this.isHotelsSearch) {
      const url = `${this.windowService.nativeWindow.location.origin}/new/tour/hotel-accommodation?tour=${encodeURIComponent(tourId)}&from=share`;
      const shareData = {
        title: 'HT.KZ',
        text: this.getTourShareText,
        url,
      };

      if (this.windowService.nativeWindow.navigator.share) {
        this.windowService.nativeWindow.navigator.share(shareData)
          .catch(() => {
            this.copyToClipBoard(shareData.url);
            alert('Ссылка скопирована в буфер обмена.');
          });
      } else {
        this.copyToClipBoard(shareData.url);
        alert('Ссылка скопирована в буфер обмена.');
      }

      this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_tour_share');
      return;
    }

    // todo: 1. переделать, чтобы вместе со ссылкой возвращался и текст для шаринга
    //  2. локадер, пока ссылку на тур получаем: перекладка тура и прочее - тоже время
    this.getTourLink(tourId).subscribe({
      next: (url) => {
        const shareData = {
          title: 'HT.KZ',
          text: this.getTourShareText,
          url,
        };

        // TODO: оформить в сервис, нельзя юзать напрямую
        if (this.windowService.nativeWindow.navigator.share) {
          this.windowService.nativeWindow.navigator.share(shareData)
            .catch((error) => {
              this.copyToClipBoard(shareData.url);
              alert('Ссылка скопирована в буфер обмена.');
            });
        } else {
          this.copyToClipBoard(shareData.url);
          alert('Ссылка скопирована в буфер обмена.');
        }
      },
      error: (err) => {
        console.error('Error fetching tour link:', err);
        alert('Не удалось получить ссылку. Попробуйте позже.');
      },
    });

    this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_tour_share');
  }

  private getTourLink(tourId: string): Observable<string> {
    return this.http
      .get<{ responseData: { link?: string }, success: boolean }>(`/new/hotels/tour-link?tourId=${tourId}`)
      .pipe(
        map((result) => {
          if (result.success && result.responseData.link) {
            return result.responseData.link;
          } else {
            throw new Error('Link not found or operation failed');
          }
        }),
        catchError((error) => {
          console.error('Error fetching tour link:', error);
          return throwError(() => new Error('Failed to fetch the tour link. Please try again later.'));
        })
      );
  }

  private copyToClipBoard(text) {
    const bufferField = document.createElement('textarea');
    document.body.appendChild(bufferField);
    bufferField.value = text;
    bufferField.select();
    document.execCommand('copy');
    document.body.removeChild(bufferField);
  }

  public get getTourShareText() {
    const hotel = this.hotelToursPageConfig.hotel;
    const searchParams = this.searchService.getLastSearchParams();
    const offerText = this.isHotelsSearch ? 'Посмотреть цену на проживание, по ссылке: ' : 'Посмотреть цену на тур, по ссылке: ';

    return hotel.hotelName + ' (' + hotel.countryName + ', ' + hotel.regionName + ')'
      + ' вылет ' + this.checkInDate
      + ', ' + (this.tour.nights)
      + ' ' + pluralize(this.tour.nights, ['ночь', 'ночи', 'ночей'])
      + ', ' + searchParams.adults + ' взр'
      + (
        searchParams.childAges.length > 0
          ? (', ' + searchParams.childAges.length + ' реб (возраст: ' + searchParams.childAges + ')')
          : ''
      )
      + (this.displayedAirlines[0]?.name ? (', ' + this.displayedAirlines[0].name) : '')
      + '. ' + offerText;
  }

  public get getShareImagePath() {
    const nativeWindow = this.windowService.nativeWindow;
    const inBrowser = typeof nativeWindow !== 'undefined';
    const UA = inBrowser ? nativeWindow.navigator.userAgent.toLowerCase() : '';
    const isIOS = /iphone|ipad|ipod|ios/.test(UA);
    const assetsBasePath = nativeWindow?.app?.theme === 'tickets'
      ? '/x-assets/img/themes/tickets-kz'
      : '/x-assets/img/icons';

    return isIOS
      ? `${assetsBasePath}/share-icon-android.svg`
      : `${assetsBasePath}/share-icon-ios.svg`;
  }

  trackByAirlineId(index: number, airline: any): any {
    return airline.code || airline.name || index;
  }

  get displayedAirlines(): HotelToursResultTourAirline[] {
    const airlines = this.tour?.flights?.airlines || [];
    const selectedAirlines = this.searchService.getLastSearchParams()?.airlines || [];

    if (!selectedAirlines.length) {
      return airlines;
    }

    const normalizedSelectedAirlines = new Set(selectedAirlines.map((value) => value.toUpperCase()));
    const filteredAirlines = airlines.filter((airline) => normalizedSelectedAirlines.has((airline.code || '').toUpperCase()));

    return filteredAirlines.length ? filteredAirlines : airlines;
  }

  openBookingPage(event?: Event) {
    event?.stopPropagation();

    const win = this.windowService.nativeWindow;
    win.brxTracking.sendAnalyticsEvent('click_tour_select');

    if (this.isHotelsSearch) {
      const params = this.searchService.getLastSearchParams();
      const dateFrom = new Date(params.dateFrom);
      const dateFromToString = dateFrom.toISOString().split('T')[0];

      const dateTo = new Date(params.dateTo);
      const dateToToString = dateTo.toISOString().split('T')[0];

      const bookingParams = new URLSearchParams(
        Object.entries({
          adult: params.adults,
          child: params.childAges.length,
          childAges: params.childAges,
          splitRooms: params.splitRooms ? 1 : 0,
          country: params.countryId,
          departCityId: params.departCityId,
          meals: params.meals,
          nightsFrom: params.nightsFrom,
          nightsTo: params.nightsTo,
          daysFrom: params.daysFrom,
          daysTo: params.daysTo,
          dateFrom: dateFromToString,
          dateTo: dateToToString,
          delta: 1,
          hotelsSearch: 1,
          from: params.from ? params.from : 'hotel-page',
          forceDates: params.forceDates ? params.forceDates : 0,
        }).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = String(value);
          }
          return acc;
        }, {} as Record<string, string>)
      );

      win.location.href = `${win.app.links.booking}/${this.tour.id}?${bookingParams.toString()}`;
      return;
    }

    const params = this.searchService.getLastSearchParams();
    const dateFrom = new Date(params.dateFrom);
    const dateFromToString = dateFrom.toISOString().split('T')[0];

    const dateTo = new Date(params.dateTo);
    const dateToToString = dateTo.toISOString().split('T')[0];

    win.eventDispatcher.dispatchEvent('_block-page');

    const newParams = {
      adult: params.adults,
      airlines: params.airlines,
      child: params.childAges.length,
      childAges: params.childAges,
      splitRooms: params.splitRooms ? 1 : 0,
      country: params.countryId,
      departCityId: params.departCityId,
      meals: params.meals,
      nightsFrom: params.nightsFrom,
      nightsTo: params.nightsTo,
      dateFrom: dateFromToString,
      dateTo: dateToToString,
      delta: 1,
      from: params.from ? params.from : 'tour-list',
      forceDates: params.forceDates ? params.forceDates : 0,
    };

    const searchParams = new URLSearchParams(
      Object.entries(newParams).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {} as Record<string, string>)
    );

    win.location.href = win.app.links.booking + '/' + this.tour.id + '?' + searchParams.toString();
  }

  openDetailsPage() {
    const win = this.windowService.nativeWindow;

    if (this.isHotelsSearch) {
      const params = new URLSearchParams({
        tour: this.tour.id,
        from: 'hotel-tour-list',
      });
      win.location.href = `/new/tour/hotel-accommodation?${params.toString()}`;
      return;
    }

    this.openBookingPage();
  }

  public get isFewAvailableSeats() {
    if (this.tour.flights?.seats) {
      return this.tour.flights.seats.some(
          (seat) =>
              seat.class === HotelToursResultResponseClassAvailable.economy &&
              seat.availability.forward === HotelToursResultResponseSeatAvailable.few
      );
    }

    return '';
  }

  get isHotelsSearch(): boolean {
    return !!this.searchService.getLastSearchParams()?.hotelsSearch;
  }

  get transportLabel(): string {
    return this.isHotelsSearch ? 'Проживание' : 'Перелет';
  }

  get transportValue(): string {
    return this.isHotelsSearch ? 'Без перелета' : 'Перелет включен';
  }

  public getRooms() {
    const roomCount = this.tour.rooms.length;

    if (roomCount === 1) {
      return `${this.tour.rooms[0].name}`;
    } else if (roomCount > 1) {
      let room = `${roomCount} номера: `;

      let roomNames = '';
      let previousName = '';
      for(let room of this.tour.rooms) {
        if (room.name !== previousName) {
          if (roomNames) {
            roomNames += ' + ';
          }
          roomNames += room.name;
          previousName = room.name;
        }
      }

      return room + roomNames;
    }
  }
}
